import { onDestroy } from "@/modules/_";
import gsap, { reduced } from "@lib/gsap";

// Careers "life" list. Two behaviors:
//  1) Single-open accordion — the answer panel animates height 0 ↔ auto (GSAP
//     measures the auto height and leaves it responsive once open). Only one
//     item is open at a time.
//  2) Directional wipe visual — one shared image stack (img-item × N) sits in
//     the left column, ordered to match the list items by index. Opening an
//     item (click / keyboard) wipes its image OVER the current one with a
//     clip-path inset, while the inner <img> counter-slides (parallax) so it
//     reads as a mask sweeping across a held image. The sweep direction follows
//     travel up/down the list. The previously-active image sits beneath as the
//     base. Hovering does NOT change the image — only opening an item does.
const DURATION = 0.5;
const EASE = "power3.out";

// Directional wipe. clip-path insets reveal the incoming image from one edge;
// the inner <img> counter-translates + un-zooms for parallax depth.
const WIPE_DUR = 0.7;
const WIPE_EASE = "expo.out";
const PARALLAX = 10; // % the inner image counter-slides during the wipe
const IMG_SCALE = 1.06; // inner image starts slightly zoomed, settles to 1
const CLIP_OPEN = "inset(0% 0% 0% 0%)"; // fully revealed
const CLIP_FROM_BOTTOM = "inset(100% 0% 0% 0%)"; // window grows up from bottom edge
const CLIP_FROM_TOP = "inset(0% 0% 100% 0%)"; // window grows down from top edge

let uid = 0; // unique-id seed across module instances (for aria-controls)

export default function (element: HTMLElement, _dataset: DOMStringMap) {
  const items = Array.from(
    element.querySelectorAll<HTMLElement>('[life-list="item"]'),
  );
  if (!items.length) return;

  const dur = reduced ? 0 : DURATION;
  const instanceId = uid++;

  // Shared image stack — index-matched to the list items. Each img-item holds
  // one <img>; the item carries the clip-path, the <img> carries the parallax.
  const imgs = Array.from(
    element.querySelectorAll<HTMLElement>('[life-list="img-item"]'),
  );
  const imgInners = imgs.map((it) => it.querySelector<HTMLElement>("img"));
  if (imgs.length) {
    gsap.set(imgs, { clipPath: CLIP_OPEN, autoAlpha: 1, zIndex: 0 });
    gsap.set(imgInners.filter(Boolean), {
      yPercent: 0,
      scale: 1,
      transformOrigin: "center center",
    });
  }
  let activeImg = -1;

  // Wipe the stack to image `i`. The incoming image is raised above the rest and
  // clipped in from an edge (bottom when moving down the list, top when moving
  // up); its inner <img> counter-slides for parallax. The previously-active
  // image stays directly beneath as the base revealed under the wipe. z is kept
  // bounded (incoming 2, previous 1, rest 0) so a higher-z overlay — e.g. the
  // heart badge — always sits above the photos.
  const setActiveImage = (i: number, immediate = false) => {
    if (!imgs.length || i === activeImg) return;
    const next = imgs[i];
    if (!next) return;
    const inner = imgInners[i];
    const prev = activeImg;

    imgs.forEach((im, idx) => {
      im.style.zIndex = idx === i ? "2" : idx === prev ? "1" : "0";
    });

    const goingDown = prev === -1 || i > prev;
    const dir = goingDown ? 1 : -1;

    if (reduced || immediate) {
      gsap.set(next, { clipPath: CLIP_OPEN, autoAlpha: 1 });
      if (inner) gsap.set(inner, { yPercent: 0, scale: 1 });
      activeImg = i;
      return;
    }

    gsap.set(next, {
      clipPath: dir === 1 ? CLIP_FROM_BOTTOM : CLIP_FROM_TOP,
      autoAlpha: 1,
    });
    gsap.to(next, {
      clipPath: CLIP_OPEN,
      duration: WIPE_DUR,
      ease: WIPE_EASE,
      overwrite: true,
    });
    if (inner) {
      gsap.fromTo(
        inner,
        { yPercent: dir * PARALLAX, scale: IMG_SCALE },
        {
          yPercent: 0,
          scale: 1,
          duration: WIPE_DUR,
          ease: WIPE_EASE,
          overwrite: true,
        },
      );
    }
    activeImg = i;
  };

  type Entry = {
    item: HTMLElement;
    trigger: HTMLElement;
    answer: HTMLElement;
  };

  const entries: Entry[] = [];
  let openIndex = -1;

  const setOpen = (i: number, open: boolean, immediate = false) => {
    const { item, trigger, answer } = entries[i];
    item.classList.toggle("is-open", open);
    trigger.setAttribute("aria-expanded", String(open));
    answer.setAttribute("aria-hidden", String(!open));

    const duration = immediate ? 0 : dur;
    gsap.to(answer, {
      height: open ? "auto" : 0,
      duration,
      ease: EASE,
      overwrite: true,
    });
  };

  const toggle = (i: number) => {
    if (openIndex === i) {
      setOpen(i, false);
      openIndex = -1;
      return;
    }
    if (openIndex !== -1) setOpen(openIndex, false); // only one open at a time
    setOpen(i, true);
    openIndex = i;
    setActiveImage(i); // the photo follows the opened item (not hover)
  };

  const unsubs: Array<() => void> = [];

  items.forEach((item, i) => {
    const trigger = item.querySelector<HTMLElement>('[life-list="trigger"]');
    // NOTE: source markup spells this attribute "answeer" — match it exactly.
    const answer = item.querySelector<HTMLElement>('[life-list="answeer"]');
    if (!trigger || !answer) return;

    gsap.set(answer, { height: 0 }); // CSS already clips overflow on the panel

    // A11y: make the div behave like a disclosure button.
    const panelId = answer.id || `life-list-${instanceId}-${i}`;
    answer.id = panelId;
    trigger.setAttribute("role", "button");
    trigger.setAttribute("tabindex", "0");
    trigger.setAttribute("aria-controls", panelId);
    trigger.setAttribute("aria-expanded", "false");
    answer.setAttribute("aria-hidden", "true");
    trigger.style.cursor = "pointer";

    entries.push({ item, trigger, answer });
    const index = entries.length - 1;

    const onClick = () => toggle(index);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault(); // Space would otherwise scroll the page
        toggle(index);
      }
    };
    trigger.addEventListener("click", onClick);
    trigger.addEventListener("keydown", onKey);

    unsubs.push(() => {
      trigger.removeEventListener("click", onClick);
      trigger.removeEventListener("keydown", onKey);
    });
  });

  // Open the first item by default (instant, no load flash) as an affordance,
  // and show its image straight away.
  if (entries.length) {
    setOpen(0, true, true);
    openIndex = 0;
    setActiveImage(0, true);
  }

  onDestroy(() => {
    unsubs.forEach((off) => off());
    entries.forEach(({ answer }) => {
      gsap.killTweensOf(answer);
    });
    if (imgs.length) {
      gsap.killTweensOf(imgs);
      gsap.killTweensOf(imgInners.filter(Boolean));
    }
  });
}
